import cn from "@/utils/class-names";
import SimpleBar from "@/components/ui/simplebar";
import Logo from "@/components/logo";

import { Link } from "react-router-dom";
import { CarbonSidebarMenu } from "./carbon-sidebar-menu";
import ProfileCardMenu from "./profile-card-menu";

import { PiDotsThreeVerticalBold } from "react-icons/pi";
import useProfile from "@/hooks/use-profile";
import { useEffect, useMemo } from "react";
import { Roles } from "@/utilities/types/Users.ts";
import { ActionIcon, Button } from "rizzui";
import { MdClose } from "react-icons/md";
import { useDrawer } from "@/app/shared/drawer-views/use-drawer";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { TAgencySetting, TDomain } from "@/utilities/types/Setting";
import SettingsRepository from "@/utilities/repositories/Settings";
import { CgWebsite } from "react-icons/cg";
import { EFeatureType } from "@/utilities/types/AdvanceFeature";

export function CarbonDrawerSidebar({ className }: { className?: string }) {
  const { profile } = useProfile();
  const { request: getAgencySetting, response: agencySettingData } = useApi<
    IResponseData<TAgencySetting>
  >({
    request: SettingsRepository.getAgencySetting,
  });

  const previewUrl = useMemo(() => {
    const domain = agencySettingData?.data?.domains?.find(
      (domain: TDomain) => !!domain?.primary,
    );
    if (domain) {
      return (domain as TDomain)?.name || "";
    }
    return "";
  }, [agencySettingData?.data?.domains]);

  const isDirectoryEnabled = useMemo(() => {
    return profile?.userFeatures?.some(
      (item) =>
        item.feature.type === EFeatureType.Directory && item.feature.enabled,
    );
  }, [profile?.id]);

  const showPreviewButton = useMemo(() => {
    return profile?.role === Roles.AGENCY && previewUrl && isDirectoryEnabled;
  }, [profile?.role, previewUrl, isDirectoryEnabled]);

  useEffect(() => {
    getAgencySetting();
  }, []);

  const { closeDrawer } = useDrawer();

  return (
    <aside
      className={cn(
        "fixed flex justify-between flex-col bottom-0 start-0 z-50 h-full w-[270px] border-e-2 border-gray-100 bg-transparent dark:bg-transparent 2xl:w-72",
        className,
      )}
    >
      <div className="sticky top-0 z-40 bg-transparent px-6 pb-5 pt-5 dark:bg-transparent 2xl:px-8 2xl:pt-6">
        <div className="flex justify-between items-center">
          <Link to={"/"} aria-label="Site Logo" className="text-gray-800">
            <Logo className="max-w-[155px]" />
          </Link>
          <ActionIcon onClick={closeDrawer} variant="text">
            <MdClose size={25} />
          </ActionIcon>
        </div>
      </div>

      <SimpleBar
        className={cn(
          "flex-1 overflow-auto",
          profile?.role === Roles.ADMIN && "h-[calc(100%-175px)]",
          profile?.role === Roles.AGENCY && "h-[calc(100%-75px)]",
        )}
      >
        <CarbonSidebarMenu />
      </SimpleBar>

      {showPreviewButton && (
        <Link
          to={`https://${previewUrl}`}
          target="_blank"
          className="h-auto block p-6"
        >
          {/* TODO: Link to agent's domain instead of hard code */}

          <Button
            className="h-14 w-full flex justify-start items-center text-xs sm:text-sm font-medium capitalize hover:text-black hover:bg-white border border-solid border-black transition-all"
            size="sm"
          >
            <div className="inline-flex items-center gap-x-2">
              <CgWebsite size={24} />
              Preview Website
            </div>
          </Button>
        </Link>
      )}

      <div className="relative mt-auto bg-gray-0 px-6 py-1 dark:bg-gray-100">
        {/* <WorkSpaceSwitcher
          dropdownClassName="max-w-[270px]"
          selectClassName="border-0 border-t-2 border-gray-200 rounded-none"
          suffixClassName="rotate-180"
        /> */}
        <ProfileCardMenu
          title={profile?.firstName + " " + profile?.lastName}
          designation={
            profile?.role === Roles.AGENCY
              ? profile?.company || ""
              : profile?.role
          }
          placement="top"
          avatarClassName="!w-10 !h-10"
          icon={
            <PiDotsThreeVerticalBold
              className={cn(
                "h-7 w-7 text-gray-400 transition-all group-hover:text-primary",
              )}
            />
          }
          className={cn("px-0 py-0")}
          buttonClassName="border-0 !border-t !border-gray-200 pt-5 px-0 rounded-none"
        />
      </div>
    </aside>
  );
}
